import { action, observable, makeObservable } from 'mobx';
import { eventBus, subscribe } from 'mobx-event-bus2';
import { SnapshotVersion } from '@tiptap-pro/extension-snapshot';
import type { Editor } from '@tiptap/react';
import type { Doc as YDoc } from 'yjs';

import {
  DEFAULT_DOCUMENT_SETTINGS,
  DocumentViewMode,
} from 'containers/Tiptap/components/tiptap-extension/document-settings-yjs';
import { SidebarEnum } from 'containers/Tiptap/components/tiptap-templates/types';
import { clampZoomPercent } from 'containers/Tiptap/components/tiptap-ui/zoom-controls/zoom-persistence';
import { DEFAULT_ZOOM_PERCENT } from 'containers/Tiptap/components/tiptap-ui/zoom-controls/zoom-presets';
import { EventType } from 'utils/events/constants';
import type { ActionEvent, UpdatedDocumentSavePayload } from 'utils/events/types';

export interface AIState {
  isLoading: boolean;
  errorMessage: string | null;
  response: string | null;
}

export interface SnapshotState {
  versions: SnapshotVersion[];
  currentVersion: number;
  latestVersion: number;
  autoVersioning: boolean;
}

export default class EditorStore {
  @observable aiState: AIState = {
    isLoading: false,
    errorMessage: null,
    response: null,
  };

  @observable activeSidebar: false | SidebarEnum.Versioning | SidebarEnum.Comments = false;

  @observable snapshotState: SnapshotState = {
    versions: [],
    currentVersion: 0,
    latestVersion: 0,
    autoVersioning: false,
  };

  @observable shareModalOpen = false;

  @observable publishModalOpen = false;

  @observable publishVersionNumber = 1;

  @observable isPublishing = false;

  @observable hasUnpublishedChanges = false;

  @observable snapshotReady = false;

  @observable currentEntityId: string | null = null;

  @observable lastSavedBy: { uuid: string; name: string } | null = null;

  @observable lastSavedAt: string | null = null;

  @observable editor: Editor | null = null;

  @observable ydoc: YDoc | null = null;

  @observable viewMode: DocumentViewMode = DEFAULT_DOCUMENT_SETTINGS.viewMode;

  @observable zoomPercent = DEFAULT_ZOOM_PERCENT;

  @observable isSettingsDirty = false;

  // Bumped when the Document tab transitions from hidden to visible so the
  // editor can re-run setPageFormat against the now-measurable container.
  // Pages-extension pagination computed while the editor was display:none
  // would otherwise stay stuck at the (0-width) layout until refresh.
  @observable documentTabReflowTick = 0;

  constructor() {
    makeObservable(this);
    eventBus.register(this);
  }

  @action.bound
  setAILoading(): void {
    this.aiState = {
      isLoading: true,
      errorMessage: null,
      response: '',
    };
  }

  @action.bound
  setAIChunk(response: string): void {
    this.aiState = {
      isLoading: true,
      errorMessage: null,
      response,
    };
  }

  @action.bound
  setAISuccess(response: string): void {
    this.aiState = {
      isLoading: false,
      errorMessage: null,
      response,
    };
  }

  @action.bound
  setAIError(errorMessage: string): void {
    this.aiState = {
      isLoading: false,
      errorMessage,
      response: null,
    };
  }

  @action.bound
  resetAIState(): void {
    this.aiState = {
      isLoading: false,
      errorMessage: null,
      response: null,
    };
  }

  @action.bound
  setActiveSidebar(sidebar: false | SidebarEnum.Versioning | SidebarEnum.Comments): void {
    this.activeSidebar = sidebar;
  }

  @action.bound
  toggleSidebar(sidebar: SidebarEnum.Versioning | SidebarEnum.Comments): void {
    this.activeSidebar = this.activeSidebar === sidebar ? false : sidebar;
  }

  @action.bound
  closeSidebar(): void {
    this.activeSidebar = false;
  }

  get versions(): SnapshotVersion[] {
    return this.snapshotState.versions;
  }

  @action.bound
  setHasUnpublishedChanges(value: boolean): void {
    this.hasUnpublishedChanges = value;
  }

  private snapshotReadyTimeout: ReturnType<typeof setTimeout> | null = null;

  @action.bound
  setSnapshotState(state: SnapshotState): void {
    this.snapshotState = state;

    if (!this.snapshotReady) {
      if (state.versions.length > 0) {
        // Versions loaded — ready
        if (this.snapshotReadyTimeout) clearTimeout(this.snapshotReadyTimeout);
        this.snapshotReady = true;
      } else if (!this.snapshotReadyTimeout) {
        // Defer ready signal to allow versions to load.
        // For new documents (no versions), this timeout ensures we still become ready.
        this.snapshotReadyTimeout = setTimeout(
          action(() => {
            if (!this.snapshotReady) {
              this.snapshotReady = true;
            }
          }),
          2000
        );
      }
    }
  }

  @action.bound
  setShareModalOpen(open: boolean): void {
    this.shareModalOpen = open;
  }

  @action.bound
  openShareModal(): void {
    this.shareModalOpen = true;
  }

  @action.bound
  closeShareModal(): void {
    this.shareModalOpen = false;
  }

  @action.bound
  toggleShareModal(): void {
    this.shareModalOpen = !this.shareModalOpen;
  }

  @action.bound
  setPublishModalOpen(open: boolean): void {
    this.publishModalOpen = open;
  }

  @action.bound
  openPublishModal(versionNumber: number): void {
    this.publishVersionNumber = versionNumber;
    this.publishModalOpen = true;
  }

  @action.bound
  closePublishModal(): void {
    this.publishModalOpen = false;
  }

  @action.bound
  setIsPublishing(value: boolean): void {
    this.isPublishing = value;
  }

  @action.bound
  setCurrentEntityId(id: string | null): void {
    this.currentEntityId = id;
  }

  @action.bound
  setEditor(editor: Editor | null): void {
    this.editor = editor;
  }

  @action.bound
  setYDoc(ydoc: YDoc | null): void {
    this.ydoc = ydoc;
  }

  @action.bound
  setViewMode(viewMode: DocumentViewMode): void {
    this.viewMode = viewMode;
  }

  @action.bound
  setZoomPercent(zoomPercent: number): void {
    this.zoomPercent = clampZoomPercent(zoomPercent);
  }

  @action.bound
  setSettingsDirty(dirty: boolean): void {
    this.isSettingsDirty = dirty;
  }

  @action.bound
  bumpDocumentTabReflowTick(): void {
    this.documentTabReflowTick += 1;
  }

  @subscribe(EventType.UpdatedDocumentSave)
  @action.bound
  updatedDocumentSave({ payload }: ActionEvent<UpdatedDocumentSavePayload>): void {
    if (payload.documentUuid !== this.currentEntityId) {
      return;
    }
    this.lastSavedBy = payload.lastSavedBy;
    this.lastSavedAt = payload.lastSavedAt;
  }

  @action.bound
  reset(): void {
    this.aiState = {
      isLoading: false,
      errorMessage: null,
      response: null,
    };
    this.activeSidebar = false;
    this.snapshotState = {
      versions: [],
      currentVersion: 0,
      latestVersion: 0,
      autoVersioning: false,
    };
    this.shareModalOpen = false;
    this.publishModalOpen = false;
    this.publishVersionNumber = 1;
    this.isPublishing = false;
    this.hasUnpublishedChanges = false;
    this.snapshotReady = false;
    this.currentEntityId = null;
    this.lastSavedBy = null;
    this.lastSavedAt = null;
    this.editor = null;
    this.ydoc = null;
    this.isSettingsDirty = false;
    this.documentTabReflowTick = 0;
    this.zoomPercent = DEFAULT_ZOOM_PERCENT;
    if (this.snapshotReadyTimeout) {
      clearTimeout(this.snapshotReadyTimeout);
      this.snapshotReadyTimeout = null;
    }
  }
}
